import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Form, Input, Button } from '../../core/styles/styledComponents';
import styled from 'styled-components';
import { theme } from '../../core/styles/theme';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../core/hooks/useToast';

const Logo = styled.img`
  height: 50px;
  margin: ${theme.spacing.lg} 0;
`;

const ForgotPasswordContainer = styled(Container)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.background.default};
`;

const ForgotPasswordCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  margin-top: ${theme.spacing.lg};
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.danger.main};
  font-size: ${theme.typography.body.fontSize};
  margin-top: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.sm};
`;

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { forgotPassword, isLoading } = useAuth();
  const { error } = useToast();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');

    if (!email) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      await forgotPassword(email);
      navigate('/forgot-password/check-email');
    } catch (err) {
      // Error is handled by AuthContext - always show generic message
      error('If an account exists for this email, you will receive a password reset link.');
    }
  };

  return (
    <ForgotPasswordContainer>
      <Logo src="/EureekaBi.png" alt="EureekaBI Logo" />
      <ForgotPasswordCard>
        <h2 style={{ marginBottom: theme.spacing.lg }}>Forgot Password</h2>
        <p style={{ marginBottom: theme.spacing.lg, textAlign: 'center' }}>
          Enter your email address and we'll send you instructions to reset your password.
        </p>
        <Form onSubmit={handleEmailSubmit}>
          <Input
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {emailError && <ErrorMessage>{emailError}</ErrorMessage>}
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Reset Instructions'}
          </Button>
          <Button type="button" variant="minimal" onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </Form>
      </ForgotPasswordCard>
    </ForgotPasswordContainer>
  );
};

export default ForgotPasswordPage; 